import type { Node as PMNode, ResolvedPos, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables/cell-selection';
import { TableMap } from '@atlaskit/editor-tables/table-map';

import { getPositionNodeTypeDepth, isWithinSameCell } from './position-checks';

function getWordsFromTextUsingSegmenter(
	text: string,
	locale: string,
): Array<{ index: number; text: string }> {
	const segmenter = new Intl.Segmenter(locale, { granularity: 'word' });
	const segments = Array.from(segmenter.segment(text));
	const words = segments.filter((segment) => segment.isWordLike);
	return words.map((word) => ({ text: word.segment, index: word.index }));
}
function getWordsFromTextUsingRegex(text: string): Array<{ index: number; text: string }> {
	const result: Array<{ index: number; text: string }> = [];
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	const regex = /\S+/gu;
	const matches = text.matchAll(regex);
	for (const match of matches) {
		result.push({ text: match[0], index: match.index });
	}
	return result;
}

function getWordsFromText(text: string, locale: string): Array<{ index: number; text: string }> {
	if (Intl.Segmenter) {
		return getWordsFromTextUsingSegmenter(text, locale);
	}
	return getWordsFromTextUsingRegex(text);
}

type SelectionPositions = { endPos: ResolvedPos; startPos: ResolvedPos };

type SelectionTransformerContext = {
	locale: string;
	selection: Selection;
};

type SelectionTransformer = (
	selectionPositions: SelectionPositions,
	context: SelectionTransformerContext,
) => SelectionPositions;

const expandSelectionToCompleteWords: SelectionTransformer = (selectionPositions, { locale }) => {
	const startPos = selectionPositions.startPos.pos;
	const endPos = selectionPositions.endPos.pos;
	const doc = selectionPositions.startPos.doc;
	let newStartPos = startPos;
	let newEndPos = endPos;

	let startPosTextNode: PMNode | undefined;
	let endPosTextNode: PMNode | undefined;
	let startPosTextNodePos: number | undefined;
	let endPosTextNodePos: number | undefined;
	doc.nodesBetween(startPos, endPos, (node, nodePos) => {
		if (!startPosTextNode && node.isText) {
			startPosTextNode = node;
			startPosTextNodePos = nodePos;
		}
		if (node.isText) {
			endPosTextNode = node;
			endPosTextNodePos = nodePos;
		}
	});

	if (startPosTextNode && startPosTextNode.isText && startPosTextNode.text && startPosTextNodePos) {
		const words = getWordsFromText(startPosTextNode.text, locale);
		const startPosInText = startPos - startPosTextNodePos;
		const selectionStartWord = words.find(
			(word) => word.index < startPosInText && word.index + word.text.length > startPosInText,
		);
		newStartPos = selectionStartWord ? selectionStartWord.index + startPosTextNodePos : startPos;
	}

	if (endPosTextNode && endPosTextNode.isText && endPosTextNode.text && endPosTextNodePos) {
		const words = getWordsFromText(endPosTextNode.text, locale);
		const endPosInText = endPos - endPosTextNodePos;
		const selectionEndWord = words.find(
			(word) => word.index < endPosInText && word.index + word.text.length > endPosInText,
		);
		newEndPos = selectionEndWord
			? selectionEndWord.index + selectionEndWord.text.length + endPosTextNodePos
			: endPos;
	}
	return { startPos: doc.resolve(newStartPos), endPos: doc.resolve(newEndPos) };
};

/**
 * If selection starts in caption, we need to wrap it into a valid media parent node as
 * it is the only way to inject it back into the doc.
 */
const expandSelectionToMediaIfInCaption: SelectionTransformer = (
	selectionPositions: SelectionPositions,
) => {
	const { startPos, endPos } = selectionPositions;
	const doc = startPos.doc;

	if (startPos.parent.type.name === 'caption') {
		return {
			startPos: doc.resolve(startPos.start(Math.max(startPos.depth - 1, 1))),
			endPos: doc.resolve(endPos.end(Math.max(endPos.depth, 1))),
		};
	}

	return selectionPositions;
};

/**
 * This transformer will expand the selection to the nearest ancestor block.
 * @see https://hello.atlassian.net/wiki/spaces/ac8/pages/5422770750/Selection+Streaming
 */
const expandSelectionToCommonBlockAncestor: SelectionTransformer = (
	selectionPositions: SelectionPositions,
) => {
	const { startPos, endPos } = selectionPositions;
	const doc = startPos.doc;

	if (startPos.parent !== endPos.parent) {
		// We want to quickly scan the range and check that the range encapsulates only specific items. If it doesn't
		// then we want to expand the selection to the nearest ancestor block.
		let expandRange = false;
		doc.nodesBetween(startPos.pos, endPos.pos, (node) => {
			// We only want to expand the selection if the selection contains anything other than an inline element or a paragraph
			// This means this is a list of what is acceptable to be in a selection and not trigger an expansion of the selection.
			if (!(node.isInline || node.type.name === 'paragraph')) {
				expandRange = true;
			}
			return !expandRange;
		});

		if (expandRange) {
			const nodeRange = startPos.blockRange(endPos);
			if (!!nodeRange) {
				return {
					startPos: doc.resolve(nodeRange.start),
					endPos: doc.resolve(nodeRange.end),
				};
			}
		}
	}

	return selectionPositions;
};

const expandSelectionToNearestBlockNode: SelectionTransformer = (
	selectionPositions: SelectionPositions,
) => {
	const { startPos, endPos } = selectionPositions;
	const doc = startPos.doc;

	const { paragraph, tableRow } = doc.type.schema.nodes;

	if (startPos.parent !== endPos.parent) {
		/**
		 * We treat cells as a defining range so if the selection starts and ends in the same cell,
		 * we simply expand to the start and end of the position's blocks.
		 */
		const isSameCell = isWithinSameCell({ $from: startPos, $to: endPos, schema: doc.type.schema });
		if (isSameCell) {
			return {
				startPos: doc.resolve(startPos.start()),
				endPos: doc.resolve(endPos.end()),
			};
			const sharedDepth = startPos.sharedDepth(endPos.pos);
			return {
				startPos: doc.resolve(startPos.start(sharedDepth)),
				endPos: doc.resolve(endPos.end(sharedDepth)),
			};
		}
		// We want to quickly scan the range and check that the range encapsulates only specific items. If it doesn't
		// then we want to expand the selection to the nearest ancestor block.
		let expandRange = false;
		doc.nodesBetween(startPos.pos, endPos.pos, (node) => {
			// We only want to expand the selection if the selection contains anything other than an inline element or a paragraph
			// This means this is a list of what is acceptable to be in a selection and not trigger an expansion of the selection.
			if (!(node.isInline || node.type === paragraph)) {
				expandRange = true;
			}
			return !expandRange;
		});

		if (expandRange) {
			let newStartPos = doc.resolve(startPos.start(Math.max(startPos.depth - 1, 1)));
			let newEndPos = doc.resolve(endPos.end());

			/**
			 * If we are expanding the range, if either end are inside a table we want to expand to the full row.
			 * This is important for streaming stability as it simplifies some of the conditions for streaming.
			 */
			const startPosTableRowDepth = getPositionNodeTypeDepth({
				resolvedPos: newStartPos,
				nodeType: tableRow,
			});
			if (startPosTableRowDepth) {
				// Extend by -1 to fully include the table row
				newStartPos = doc.resolve(newStartPos.start(startPosTableRowDepth) - 1);
			}

			const endPosTableRowDepth = getPositionNodeTypeDepth({
				resolvedPos: newEndPos,
				nodeType: tableRow,
			});
			if (endPosTableRowDepth) {
				// Extend by +1 to fully include the table row
				newEndPos = doc.resolve(newEndPos.end(endPosTableRowDepth) + 1);
			}

			return {
				startPos: newStartPos,
				endPos: newEndPos,
			};
		}
	}
	return selectionPositions;
};

const lookBackwardsForUnmergedRow = (mapByRow: number[][], startingIndex: number) => {
	for (let i = startingIndex; i > 0; i--) {
		// We keep checking the next row to see if there are any duplicate positions
		// If there are, it means that there are vertically merged cells and we want to extend the position
		const rowA = mapByRow[i - 1];
		const rowB = mapByRow[i];

		if (rowA.every((val, idx) => val !== rowB[idx])) {
			return i;
		}
	}
	return 0;
};

const lookAheadForUnmergedRow = (mapByRow: number[][], startingIndex: number) => {
	for (let i = startingIndex; i < mapByRow.length - 1; i++) {
		// We keep checking the next row to see if there are any duplicate positions
		// If there are, it means that there are vertically merged cells and we want to extend the position
		const rowA = mapByRow[i];
		const rowB = mapByRow[i + 1];

		if (rowA.every((val, idx) => val !== rowB[idx])) {
			return i;
		}
	}
	/**
	 * If the table is one row high OR everything is merged, we just return the last row
	 */
	return mapByRow.length - 1;
};

const getCellPosFromDirectDescendant = (schema: Schema, $pos: ResolvedPos) => {
	const { tableCell, tableHeader } = schema.nodes;
	for (let depth = $pos.depth; depth > 0; depth--) {
		if ([tableCell, tableHeader].includes($pos.node(depth).type)) {
			return $pos.start(depth - 1);
		}
	}
};
/**
 * Transforms a CellSelection's positions into normal ProseMirror positions.
 */
const transformCellSelectionToTextRange: SelectionTransformer = (selectionPositions, context) => {
	let selection = context.selection;

	const { startPos, endPos } = selectionPositions;
	const { doc } = startPos;
	const { table, tableRow } = doc.type.schema.nodes;
	if (
		!(selection instanceof CellSelection) &&
		[table, tableRow].includes(startPos.node(startPos.sharedDepth(endPos.pos)).type)
	) {
		/**
		 * If a selection spans across the same table, we want to convert it into a CellSelection.
		 * CellSelections aren't always created when dragging across cells with multiple child blocks for some reason...
		 */
		const anchorCellPos = getCellPosFromDirectDescendant(doc.type.schema, startPos);
		const headCellPos = getCellPosFromDirectDescendant(doc.type.schema, endPos);

		if (anchorCellPos && headCellPos) {
			selection = CellSelection.create(doc, anchorCellPos, headCellPos);
		}
	}

	if (selection instanceof CellSelection) {
		const { $anchorCell, $headCell } = selection;

		if ($headCell.pos === $anchorCell.pos) {
			/**
			 * If the selection is a single cell selection, use the inner positions
			 */
			return {
				startPos: doc.resolve($anchorCell.pos + 1),
				endPos: doc.resolve($anchorCell.pos + ($anchorCell.nodeAfter?.nodeSize || 0) - 1),
			};
		}
		const tableNode = $anchorCell.node(-1);
		const tablePos = $anchorCell.start(-1);
		const map = TableMap.get(tableNode);
		const { mapByRow } = map;

		const rowIndexContainingAnchorCell = mapByRow.findIndex((r) =>
			r.includes($anchorCell.pos - tablePos),
		);
		const rowIndexContainingHeadCell = mapByRow.findIndex((r) =>
			r.includes($headCell.pos - tablePos),
		);

		let smallerRowIndex = Math.min(rowIndexContainingAnchorCell, rowIndexContainingHeadCell);
		let largerRowIndex = Math.max(rowIndexContainingAnchorCell, rowIndexContainingHeadCell);

		if (smallerRowIndex === -1 || largerRowIndex === -1) {
			// we should always be able to find the rows, but if not, we return the original positions
			return selectionPositions;
		}

		smallerRowIndex = lookBackwardsForUnmergedRow(mapByRow, smallerRowIndex);
		largerRowIndex = lookAheadForUnmergedRow(mapByRow, largerRowIndex);

		const startingCellIndex = Math.min(...mapByRow[smallerRowIndex]);
		const endingCellIndex = Math.max(...mapByRow[largerRowIndex]);

		const { map: allCells } = map;
		const veryFirstCellIndex = allCells[0];
		const veryLastCellIndex = allCells[allCells.length - 1];
		const entireTableSelected =
			startingCellIndex === veryFirstCellIndex && endingCellIndex === veryLastCellIndex;

		if (entireTableSelected) {
			const tableStart = doc.resolve(tablePos - 1);

			return { startPos: tableStart, endPos: doc.resolve(tableStart.pos + tableNode.nodeSize) };
		}

		const startingCellPos = doc.resolve(tablePos + startingCellIndex);
		const endingCellPos = doc.resolve(tablePos + endingCellIndex);

		return {
			startPos: doc.resolve(startingCellPos.start(startingCellPos.depth + 1)),
			endPos: doc.resolve(endingCellPos.end(endingCellPos.depth)),
		};
	}

	return selectionPositions;
};

/**
 * We need to expand any selections that cross over to multiple columns as the
 * layout plugin automatically repairs columns, but this can mean replacing a user's
 * column outside the selection to make room for the AI generated column.
 */
const expandSelectionToEntireLayout: SelectionTransformer = (selectionPositions) => {
	const { startPos, endPos } = selectionPositions;
	const doc = startPos.doc;
	const { layoutColumn } = doc.type.schema.nodes;

	const startPosLayoutColumnDepth = getPositionNodeTypeDepth({
		resolvedPos: startPos,
		nodeType: layoutColumn,
	});

	const endPosLayoutColumnDepth = getPositionNodeTypeDepth({
		resolvedPos: endPos,
		nodeType: layoutColumn,
	});

	// If both are in layoutColumn, check if they're in the same layoutColumn
	if (startPosLayoutColumnDepth && endPosLayoutColumnDepth) {
		// Check if both positions are within the same layoutColumn by comparing
		// the start position of the layoutColumn at that depth
		const startLayoutColumnStart = startPos.start(startPosLayoutColumnDepth);
		const endLayoutColumnStart = endPos.start(endPosLayoutColumnDepth);

		if (startLayoutColumnStart === endLayoutColumnStart) {
			// Both are in the same layoutColumn, don't expand
			return selectionPositions;
		}
	}

	let newStartPos = startPos;
	let newEndPos = endPos;

	// If startPos is in layoutColumn, extend to start of layoutSection
	if (startPosLayoutColumnDepth && startPos.node(startPosLayoutColumnDepth - 1).childCount > 1) {
		newStartPos = doc.resolve(startPos.start(startPosLayoutColumnDepth - 1));
	}

	// If endPos is in layoutColumn, extend to end of layoutSection
	if (endPosLayoutColumnDepth && endPos.node(endPosLayoutColumnDepth - 1).childCount > 1) {
		newEndPos = doc.resolve(endPos.end(endPosLayoutColumnDepth - 1));
	}

	return { startPos: newStartPos, endPos: newEndPos };
};

const selectionTransformer =
	(transformers: SelectionTransformer[]) =>
	(
		selectionPositions: SelectionPositions,
		context: SelectionTransformerContext,
	): SelectionPositions => {
		return transformers.reduce(
			(newSelectionPositions, transformer) => transformer(newSelectionPositions, context),
			selectionPositions,
		);
	};

/**
 * Expands the selection to complete words for AI experience, considering locale.
 *
 * @param {Selection} selection - Selection to expand.
 * @param {string} locale - locale of the product where editor is used.
 * @returns The expanded selection.
 *
 * @example
 * ```ts
 * const expandedSelection = expandSelectionForAIExperience(state, selection, locale);
 * ```
 */
export function expandSelectionForAIExperience({
	selectionPositions,
	context,
	options,
}: {
	context: {
		locale: string;
		selection: Selection;
	};
	options?: {
		shouldExpandSelectionToCommonBlockAncestor?: boolean;
		shouldExpandSelectionToCompleteWords?: boolean;
		shouldExpandSelectionToEntireLayout?: boolean;
		shouldExpandSelectionToMediaIfInCaption?: boolean;
		shouldExpandSelectionToNearestBlockNode?: boolean;
		shouldTransformCellSelectionToTextRange?: boolean;
	};
	selectionPositions: SelectionPositions;
}): number[] {
	/**
	 * If there is no selection then don't expand it
	 */
	if (selectionPositions.startPos.pos === selectionPositions.endPos.pos) {
		return [selectionPositions.startPos.pos, selectionPositions.endPos.pos];
	}

	const { startPos, endPos } = selectionTransformer(
		[
			/**
			 * Keep transformCellSelectionToTextRange first as it converts CellSelection into regular positions.
			 * This allows further transformations to happen if desired.
			 */
			(!options || options.shouldTransformCellSelectionToTextRange) &&
				transformCellSelectionToTextRange,
			(!options || options.shouldExpandSelectionToCompleteWords) && expandSelectionToCompleteWords,
			(!options || options.shouldExpandSelectionToMediaIfInCaption) &&
				expandSelectionToMediaIfInCaption,
			(!options || options.shouldExpandSelectionToNearestBlockNode) &&
				expandSelectionToNearestBlockNode,
			(!options || options.shouldExpandSelectionToCommonBlockAncestor) &&
				expandSelectionToCommonBlockAncestor,
			(!options || options.shouldExpandSelectionToEntireLayout) && expandSelectionToEntireLayout,
		].filter((transformers) => !!transformers),
	)(selectionPositions, context);

	// Bound the positions by min and max
	const [min, max] = [0, startPos.doc.content.size];

	return [Math.max(min, startPos.pos), Math.min(max, endPos.pos)];
}
